import { BodyText, Heading, Modal } from '@farol-ds/react'
import { Image } from '@jusbrasil-web/shared-next-image'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { formatDate } from '@jusbrasil-web/shared-utils-format'
import { useEffect, useRef } from 'react'

import validatingImgSrc from '../../../assets/cpf-validation/validation.svg'
import { useValidateCPF } from '../../../hooks'
import { useModalContext } from '../../cpf-validation/context'
import { Steps } from '../../cpf-validation/enums'
import styles from './complete-signup-modal.module.scss'

// Mirrors the signup / JPV CPF flow: a CPF already verified on another account is NOT a blocker.
// `saveCpfDataOnError` persists the verification anyway, so we accept the duplication and route to
// success; only a bureau mismatch (`CPFMatch` — CPF + birth date don't belong together) is a real
// error. The reused `ValidatingIdentity` can't do this (it forces `saveCpfDataOnError: false` and
// sends `CPFInUse` to the transfer/dedup flow), hence this nudge-specific step.
export function NudgeValidatingStep() {
  const { setCurrentStep, sharedData, viewer, eventData, validationOrigin } = useModalContext()
  const validateCPF = useValidateCPF()
  const pid = viewer?.pid.toString() || null
  const startedRef = useRef(false)

  useEffect(() => {
    sendMetrics('UserAccount.ValidatingProfileViewed', pid, {
      ...eventData,
      metadata: {
        ...eventData?.metadata,
        cpf: sharedData?.cpf,
        birthDate: formatDate(sharedData?.birthDate?.getTime() || ''),
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (startedRef.current || !sharedData.cpf || !sharedData.birthDate) return
    startedRef.current = true

    validateCPF({
      cpf: sharedData.cpf,
      birthDate: sharedData.birthDate,
      origin: validationOrigin,
      saveCpfDataOnError: true,
      matchValidationFirst: true,
    })
      .then(() => setCurrentStep(Steps.IDENTITY_SUCCESS))
      .catch((error) => {
        const cpfMismatch = error?.graphQLErrors?.some(
          (e: { info: { formattedKey: string } }) => e.info.formattedKey === 'CPFMatch'
        )
        setCurrentStep(cpfMismatch ? Steps.IDENTITY_ERROR : Steps.IDENTITY_SUCCESS)
      })
  }, [sharedData, validateCPF, validationOrigin, setCurrentStep])

  return (
    <Modal.Body data-testid="complete-signup-validating-step">
      <div className={styles.illustratedHeader}>
        <Image alt="" src={validatingImgSrc} height={160} width={160} />
        <Heading size="md">Verificando identidade...</Heading>
        <BodyText size="md">Aguarde enquanto validamos as informações</BodyText>
      </div>
    </Modal.Body>
  )
}
